import { Printer } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { TenantPrinterWithDetails } from '@maker-toolchest/shared';
import { PrinterStatusBadge } from './PrinterStatusBadge';
import { usePrints } from '../hooks';
import { Button } from '@/components/Button';

interface PrinterStatusPanelProps {
  printers: TenantPrinterWithDetails[];
  isLoading?: boolean;
}

export function PrinterStatusPanel({ printers, isLoading }: PrinterStatusPanelProps) {
  const navigate = useNavigate();

  // Fetch active prints
  const { data: prints } = usePrints({
    page: 1,
    limit: 100,
    sortBy: 'startedAt',
    sortOrder: 'desc',
    status: 'IN_PROGRESS',
  });

  if (isLoading) {
    return (
      <div className="rounded border border-border bg-background p-4">
        <h2 className="mb-3 text-sm font-medium text-foreground">Printer Status</h2>
        <div className="flex items-center justify-center py-8">
          <p className="text-sm text-foreground-muted">Loading printers...</p>
        </div>
      </div>
    );
  }

  if (printers.length === 0) {
    return (
      <div className="rounded border border-border bg-background p-4">
        <h2 className="mb-3 text-sm font-medium text-foreground">Printer Status</h2>
        <div className="flex flex-col items-center justify-center gap-3 py-8 text-center">
          <Printer className="h-10 w-10 text-foreground-muted" />
          <p className="text-sm text-foreground-muted">No printers added yet</p>
          <Button
            size="sm"
            onClick={() => navigate('/printers?tab=library')}
          >
            Add Printer
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded border border-border bg-background p-4">
      <h2 className="mb-3 text-sm font-medium text-foreground">Printer Status</h2>
      <div className="overflow-hidden rounded border border-border">
        <table className="min-w-full divide-y divide-border">
          <thead className="bg-background-subtle">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-foreground-muted">
                Printer
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-foreground-muted">
                Status
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-foreground-muted">
                Current Job
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border bg-background">
            {printers.map((printer) => (
              <PrinterStatusRow
                key={printer.id}
                printer={printer}
                activePrint={prints?.find((p) => p.tenantPrinterId === printer.id)}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

interface PrinterStatusRowProps {
  printer: TenantPrinterWithDetails;
  activePrint?: any; // PrintWithRelations
}

function PrinterStatusRow({ printer, activePrint }: PrinterStatusRowProps) {
  const displayName = printer.instanceName || printer.printer.name;
  const hasActiveJob = !!activePrint;

  return (
    <tr className="hover:bg-background-subtle/50">
      <td className="whitespace-nowrap px-3 py-2">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded bg-background-subtle">
            <Printer className="h-4 w-4 text-foreground-muted" />
          </div>
          <div>
            <div className="text-sm font-medium text-foreground">{displayName}</div>
            {printer.printer.brand && (
              <div className="text-xs text-foreground-muted">
                {printer.printer.brand.name} {printer.printer.model?.name || ''}
              </div>
            )}
          </div>
        </div>
      </td>
      <td className="whitespace-nowrap px-3 py-2">
        <PrinterStatusBadge status={printer.status} />
      </td>
      <td className="px-3 py-2 text-sm text-foreground-muted">
        {hasActiveJob && activePrint ? (
          <div className="flex items-center gap-1.5">
            <div className="h-2 w-2 animate-pulse rounded-full bg-primary" />
            <span className="truncate max-w-[200px]" title={activePrint.name}>
              {activePrint.name}
            </span>
          </div>
        ) : printer.status === 'IN_SERVICE' ? (
          <span className="flex items-center gap-1.5 text-foreground-subtle">
            <span>Idle</span>
          </span>
        ) : (
          <span>—</span>
        )}
      </td>
    </tr>
  );
}
